import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { TOOLS } from "../../data/tools";
import { scrollToId } from "../../hooks/useLenis";
import { useLang } from "../../i18n/LanguageContext";
import { Info, Cpu, ArrowRight, Coins } from "lucide-react";

const COST = { text: 5, image: 20, voice: 15, video: 129 };

const ModuleCard = ({ mod, index }) => {
  const { t } = useLang();
  return (
    <motion.div
      data-testid={`module-card-${mod.lookup_key}`}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: (index % 3) * 0.08 }}
      className={`group relative flex min-h-[300px] flex-col justify-between overflow-hidden rounded-2xl border border-blue-500/15 bg-blue-400/[0.02] p-8 backdrop-blur-xl transition-[border-color,box-shadow] duration-500 hover:border-blue-400/50 hover:glow-cyan ${mod.span}`}
    >
      <div className="pointer-events-none absolute inset-0 overflow-hidden opacity-0 transition-opacity duration-500 group-hover:opacity-100">
        <div className="absolute left-0 right-0 h-24 bg-gradient-to-b from-blue-400/10 to-transparent" style={{ animation: "scanline 3.5s linear infinite" }} />
      </div>
      {mod.image && (
        <div className="absolute inset-0 z-0">
          <img src={mod.image} alt={mod.name} className="h-full w-full object-cover opacity-25 transition-all duration-700 group-hover:scale-105 group-hover:opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] via-[#0F172A]/80 to-[#0F172A]/30" />
        </div>
      )}
      <div className="relative z-10 flex items-start justify-between">
        <div className="flex items-center gap-2 rounded-full border border-blue-500/20 bg-[#0F172A]/60 px-3 py-1.5">
          <Cpu size={12} className="text-blue-400" />
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-blue-200">{mod.code}</span>
        </div>
        <span className="flex items-center gap-2 rounded-full border border-blue-500/20 px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.2em] text-blue-200">
          {t(`marketplace.kind.${mod.kind}`)} <Info size={12} className="text-[#8AA0C0]" />
        </span>
      </div>
      <div className="relative z-10">
        <h3 className="font-display text-2xl font-700 tracking-tighter text-white">{mod.name}</h3>
        <p className="mt-2 max-w-sm font-body text-sm leading-relaxed text-[#8AA0C0]">{mod.desc}</p>
        <div className="mt-6 flex items-center justify-between">
          <div className="inline-flex items-center gap-2 rounded-full border border-blue-500/20 bg-blue-500/5 px-3 py-1.5">
            <Coins size={13} className="text-blue-300" />
            <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-blue-200">{COST[mod.kind]} {t("marketplace.credits")}</span>
          </div>
          <button
            data-testid={`module-open-${mod.lookup_key}`}
            onClick={() => scrollToId("cockpit")}
            className="inline-flex items-center gap-2 rounded-full border border-blue-400/40 bg-blue-400/5 px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-blue-100 transition-all duration-300 hover:bg-blue-500 hover:text-white"
          >
            {t("marketplace.open")} <ArrowRight size={13} />
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export const Marketplace = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });
  const { t } = useLang();

  return (
    <section id="marketplace" data-testid="marketplace-section" className="relative py-28 lg:py-36">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div ref={ref} className="mb-16 max-w-3xl">
          <motion.span initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}} className="font-mono text-xs uppercase tracking-[0.35em] text-blue-400">
            [ {t("marketplace.tag")} ]
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="mt-4 font-display text-4xl font-800 tracking-tighter text-white sm:text-5xl lg:text-6xl"
          >
            {t("marketplace.title")}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="mt-5 font-body text-base leading-relaxed text-[#8AA0C0] sm:text-lg"
          >
            {t("marketplace.subtitle")}
          </motion.p>
        </div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-8 lg:grid-cols-12">
          {TOOLS.map((mod, i) => <ModuleCard key={mod.lookup_key} mod={mod} index={i} />)}
        </div>
      </div>
    </section>
  );
};
